/**
 * @fileoverview Module for `src/admin/Partners.js`.
 */

import axios from 'axios';
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  FunctionField,
  Filter,
  TextInput,
  BooleanInput,
  ImageInput,
  ImageField,
  DateInput,
  Button,
  BulkExportButton,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  Create,
  SimpleForm,
  required,
  regex,
  minLength,
  Toolbar,
  SaveButton,
  Edit,
  EditButton,
  useNotify,
} from 'react-admin';
import LaunchIcon from '@material-ui/icons/Launch';
import PeopleIcon from '@material-ui/icons/People';
import propTypes from 'prop-types';
import queryString from 'query-string';
import React, { Fragment, Component } from 'react';

import { AuthContext } from '../contexts/AuthContext';
import { currentElectionDate } from '../lib/dates';
import Users from './Users';

/**
 * Edit toolbar for partner records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Toolbar with save action.
 */
const PartnerEditToolbar = (props) => (
//removes default delete option from toolbar
  <Toolbar { ...props } >
    <SaveButton />
  </Toolbar>
);
const urlRegex = /^https?:\/\/.*$/;
const urlMessage = ' should be a url starting with https://';

/**
 * Maps partner API failures to notification messages.
 *
 * @param {Object} error
 * @param {Function} notify
 * @returns {void}
 */
const errorHandler = (error, notify) => {
  if (error.status === 'Conflict') {
    notify(`Error: Name and Slug must be Unique`);
  } else {
    notify(`Error: ${ error.body || error.message }`);
  }
};

/**
 * Optional partner inputs used in create and edit forms.
 *
 * @returns {JSX.Element} Optional partner controls.
 */
const OptionalInputs = () => (
  <Fragment>
    <TextInput
      source='partnerUrl'
      validate={ [regex(urlRegex, `The partner url ${ urlMessage }`)] }
    />
    <br />
    <BooleanInput helperText="Turn public 'on' when the partner should become visible on the Internet." source='public' />
    <BooleanInput helperText='Show or hide on Big Send' source='showForBigsend' />
    <BooleanInput helperText='Show or hide on Big Send At Work' source='showForBigsendAtWork' />
    <BooleanInput helperText='Show or hide on Big Send On Campus' source='showForBigsendOnCampus' />
    <BooleanInput helperText='Show or hide landing page' source='hideLandingPage' />
    <BooleanInput source='hideSwingLeftOptIn' />
    <BooleanInput source='nonPartisan' />
    <ReferenceArrayInput
      allowEmpty
      filter={{ 'electionDate:greaterThanOrEqualTo': currentElectionDate() }}
      helperText='Districts to show on the partner landing page.'
      label='districts'
      perPage={ 100 }
      reference='Districts'
      source='districtIds'
    >
      <AutocompleteArrayInput optionText='districtId' />
    </ReferenceArrayInput>
  </Fragment>
);


/**
 * Edit view for partner records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Partner editor.
 */
export const PartnerEdit = (props) => {
  const notify = useNotify();
  /**
   * Delegates partner edit errors to the shared error handler for admin notification.
   */
  const onFailure = (error) => {
    errorHandler(error, notify);
  };

  return (
    <Edit onFailure={ onFailure } undoable={ false } { ...props }>
      <SimpleForm redirect='edit' toolbar={ <PartnerEditToolbar /> }>
        <TextInput disabled source='id' />
        <TextInput disabled source='slug' />
        <TextInput
          disabled
          label='Agreement Signed By'
          source='signedBy'
        />
        <DateInput
          disabled
          label='Agreement Signed At'
          source='signedAt'
        />
        <TextInput
          label='Partner Agreement Override'
          source='agreementOverride'
        />
        <TextInput
          source='name'
          validate={ [required(), minLength(3)] }
        />
        <FunctionField
          label='Existing Logo'
          render={ (record) => (
            record.logo
              ? (
                <Fragment>
                  <img alt='' className='img-fluid mt-4 mt-sm-0' label='Existing Logo' src={ record.logo } />
                  <br />
                  <Button
                    color='primary'
                    href={ `/${ record.slug }?preview=1` }
                    label='Preview Partner Landing Page'
                    target='_blank'
                  />
                </Fragment>
                )
              : '(None)'
          ) }
        />
        <ImageInput accept='image/*' label='Update Logo' source='logo'>
          <ImageField source='src' />
        </ImageInput>
        <FunctionField
          label='Existing Metatag Image'
          render={ (record) => (
            record.metaTagImage
              ? (
                <img
                  alt=''
                  className='img-fluid mt-4 mt-sm-0'
                  label='Existing Metatag Image'
                  src={ record.metaTagImage }
                />
                )
              : '(None)'
          ) }
        />
        <ImageInput accept='image/*' label='Update Metatag Image' source='metaTagImage'>
          <ImageField source='src' />
        </ImageInput>
        <OptionalInputs />
      </SimpleForm>
    </Edit>
  );
};

/**
 * Toolbar used by partner create forms.
 *
 * @param {Object} props
 * @returns {JSX.Element} Create toolbar.
 */
const PartnerCreateToolbar = (props) => (
  <Toolbar { ...props } >
    <SaveButton
      label='create'
    />
  </Toolbar>
);

/**
 * Create view for partner records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Partner creation form.
 */
export const PartnerCreate = (props) => {
  const notify = useNotify();
  /**
   * Delegates partner creation errors to the shared error handler for admin notification.
   */
  const onFailure = (error) => {
    errorHandler(error, notify);
  };
  return (
    <Create onFailure={ onFailure } { ...props }>
      <SimpleForm toolbar={ <PartnerCreateToolbar /> }>
        <TextInput
          label='Partner name'
          source='name'
          validate={ [required(), minLength(3)] }
        />
        <TextInput
          helperText='This cannot be changed or deleted.'
          label='Partner slug'
          source='slug'
          validate={ [
            required(),
            regex(/^[a-z\d]*$/, 'Slug must be alphanumeric with lowercase letters'),
            regex(/[a-z]/, "Slug can't be all numbers"),
            minLength(3),
          ] }
        />
        <OptionalInputs />
      </SimpleForm>
    </Create>
  );
};

/**
 * Bulk action controls for partner lists.
 *
 * @param {Object} props
 * @returns {JSX.Element} Bulk action buttons.
 */
const PartnerListBulkActionButtons = (props) => (
  <BulkExportButton { ...props } />
);

/**
 * Filter controls for partner list queries.
 *
 * @param {Object} props
 * @returns {JSX.Element} Partner filters.
 */
const PartnerFilter = (props) => (
  <Filter { ...props }>
    <TextInput alwaysOn label='Name' source='name:contains' />
    <TextInput alwaysOn label='Slug' source='slug:contains' />
    <BooleanInput defaultValue source='public' />
    <BooleanInput defaultValue={ false } source='showForBigsend' />
    <BooleanInput defaultValue={ false } source='hideSwingLeftOptIn' />
    <BooleanInput defaultValue={ false } source='nonPartisan' />
    <BooleanInput label='Unsigned' source='signedAt' />
  </Filter>
);

/**
 * Partner list page with optional direct redirect to single-partner stats.
 */
class PartnerList extends Component {
  static contextType = AuthContext;

  /**
   * Initializes component state, extracting an optional partner slug from the URL query string.
   */
  constructor(props) {
    super(props);
    this.state = {
      partners: null,
      partner: props.location && props.location.search ? queryString.parse(this.props.location.search).partner : null,
    };
  }

  /**
   * Loads partner options and resolves single-partner redirects.
   *
   * @returns {Promise<void>} Resolves after partner data is loaded.
   */
  async componentDidMount() {
    const queryParams = this.state.partner
      ? {
          filter: JSON.stringify({ partner: this.state.partner }),
        }
      : {};

    const url = `${ process.env.REACT_APP_API_URL }/v1/partners?${ queryString.stringify(queryParams) }`;
    const params = { partnerLandingPage: true };
    const accessToken = await this.context.getAccessToken();
    const result = await axios({
      method: 'GET',
      url,
      headers: { Authorization: `Bearer ${ accessToken }` },
      params,
    });
    this.setState({
      partners: result.data,
      partner: result.data.length === 1 ? result.data[0].slug : null,
    });
  }

  /**
   * Renders the partner stats dashboard for single-partner users, or a full partner list with edit and stats links for admins.
   */
  render() {
    if (!this.state.partners) {
      return null;
    }
    // If this user only has access to a single partner, then show the partner stats dashboard for that one partner
    return this.state.partner
      ? <Users auth={ this.context } { ...this.props } partner={ this.state.partner } showHeader={ false } />
      : (
        <List { ...this.props } bulkActionButtons={ <PartnerListBulkActionButtons /> } filters={ <PartnerFilter /> } sort={{ field: 'id', order: 'DESC' }}>
          <Datagrid>
            <TextField source='id' />
            { /*<ImageField source="logo" />*/ }
            <TextField source='slug' />
            <FunctionField
              label='Name'
              render={ (record) => (
                <span>
                  { record.name }
                  <a
                    className={ record.partnerUrl && record.partnerUrl !== '' ? '' : 'hidden' }
                    href={ record.partnerUrl }
                    rel='noreferrer'
                    target='_blank'
                  >
                    <LaunchIcon fontSize='small' />
                  </a>
                </span>
              ) }
              sortBy='name'
            />
            <BooleanField source='public' />
            <DateField showTime source='createdAt' />
            <DateField showTime source='updatedAt' />
            <DateField showTime source='signedAt' />
            <FunctionField
              label='Users and Stats'
              render={ (record) => (
                <Button color='primary' href={ `/admin#/partner-users/${ record.slug }` } icon={ PeopleIcon } label='Stats' >
                  <PeopleIcon />
                </Button>
              ) }
            />
            { this.props.hasEdit ? <EditButton { ...this.props } /> : null }
          </Datagrid>
        </List>
        );
  }

  static get propTypes() {
    return {
      partner: propTypes.string,
      hasEdit: propTypes.bool,
      location: propTypes.object,
    };
  }
}

export default PartnerList;
